import { Box, Button, Input, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox, Field } from '@mangovoice/mango-ui'
import type { WebhookConfigDetail } from '@mangovoice/types'

import type { ApiError } from '../api/types'
import { brandedButtonCss, isValidHttpsUrl, REMOTE_ID_REGEX } from '../utils'

export interface WebhooksV2ConfigFormProps {
  integration: WebhookConfigDetail
  isPending: boolean
  isError: boolean
  error: ApiError | null
  onSave: (data: {
    remote_id: string
    callback_url: string
    event_selectors: string[]
  }) => void
}

export const WebhooksV2ConfigForm = ({
  integration,
  isPending,
  isError,
  error,
  onSave,
}: WebhooksV2ConfigFormProps) => {
  const { t } = useTranslation()
  const [remoteIdValue, setRemoteIdValue] = useState<string | null>(null)
  const [remoteIdError, setRemoteIdError] = useState<string | null>(null)
  const [callbackUrlValue, setCallbackUrlValue] = useState<string | null>(null)
  const [callbackUrlError, setCallbackUrlError] = useState<string | null>(null)
  const [selectedEventsValue, setSelectedEventsValue] = useState<
    string[] | null
  >(null)
  const [warnNoEvents, setWarnNoEvents] = useState(false)

  const currentRemoteId = remoteIdValue ?? integration.remote_id ?? ''
  const currentCallbackUrl =
    callbackUrlValue ?? integration.callback_url_v2 ?? ''
  const currentSelectedEvents =
    selectedEventsValue ?? integration.selected_events ?? []
  const availableEvents = integration.available_events ?? []

  const handleToggleEvent = (event: string, checked: boolean) => {
    setWarnNoEvents(false)
    setSelectedEventsValue(
      checked
        ? [...currentSelectedEvents, event]
        : currentSelectedEvents.filter((e) => e !== event),
    )
  }

  const validate = (): boolean => {
    let valid = true

    if (!REMOTE_ID_REGEX.test(currentRemoteId)) {
      setRemoteIdError(
        'Two characters minimum. Must begin and end with letter(s) or number(s). May use dash character as separator. e.g. Mango-43aC-Eaq7',
      )
      valid = false
    } else {
      setRemoteIdError(null)
    }

    const trimmedUrl = currentCallbackUrl.trim()
    if (!trimmedUrl) {
      setCallbackUrlError('Required')
      valid = false
    } else if (!isValidHttpsUrl(trimmedUrl)) {
      setCallbackUrlError('Must be a valid secure (https://) URL')
      valid = false
    } else {
      setCallbackUrlError(null)
    }

    return valid
  }

  const handleSave = () => {
    if (!validate()) return

    if (currentSelectedEvents.length === 0) {
      setWarnNoEvents(true)
      return
    }

    onSave({
      remote_id: currentRemoteId,
      callback_url: currentCallbackUrl,
      event_selectors: currentSelectedEvents,
    })
  }

  const handleConfirmedSave = () => {
    setWarnNoEvents(false)
    if (!validate()) return
    onSave({
      remote_id: currentRemoteId,
      callback_url: currentCallbackUrl,
      event_selectors: currentSelectedEvents,
    })
  }

  return (
    <Box
      maxW="560px"
      width="full"
      mx="auto"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      {/* Remote ID */}
      <Field.Root required invalid={!!remoteIdError}>
        <Field.Label>{t('webhooks.webhooksV2.remoteId')}</Field.Label>
        <Input
          value={currentRemoteId}
          onChange={(e) => {
            setRemoteIdValue(e.target.value)
            setRemoteIdError(null)
          }}
          placeholder={t('webhooks.webhooksV2.remoteIdPlaceholder')}
        />
        {remoteIdError && <Field.ErrorText>{remoteIdError}</Field.ErrorText>}
      </Field.Root>

      {/* Callback URL */}
      <Field.Root required invalid={!!callbackUrlError}>
        <Field.Label>{t('webhooks.webhooksV2.callbackUrl')}</Field.Label>
        <Input
          value={currentCallbackUrl}
          onChange={(e) => {
            setCallbackUrlValue(e.target.value)
            setCallbackUrlError(null)
          }}
          placeholder={t('webhooks.webhooksV2.callbackUrlPlaceholder')}
        />
        {callbackUrlError && (
          <Field.ErrorText>{callbackUrlError}</Field.ErrorText>
        )}
      </Field.Root>

      {/* Event Selectors */}
      <Box>
        <Text fontWeight="medium" fontSize="sm" mb={2}>
          {t('webhooks.webhooksV2.eventSelectors')}
        </Text>
        {availableEvents.length > 0 ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {availableEvents.map((event) => (
              <Checkbox
                key={event}
                id={`event-selector-${event}`}
                checked={currentSelectedEvents.includes(event)}
                onCheckedChange={({ checked }) =>
                  handleToggleEvent(event, !!checked)
                }
                display="flex"
                alignItems="center"
              >
                <Text fontSize="sm">{event}</Text>
              </Checkbox>
            ))}
          </Box>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            {t('webhooks.webhooksV2.eventSelectorsLoadError')}
          </Text>
        )}
      </Box>

      {/* No-events warning */}
      {warnNoEvents && (
        <Box
          p={3}
          borderRadius="md"
          backgroundColor="var(--chakra-colors-bg-emphasized)"
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text fontSize="sm">{t('webhooks.webhooksV2.noEventsWarning')}</Text>
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setWarnNoEvents(false)}
            >
              {t('webhooks.webhooksV2.goBack')}
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmedSave}
              loading={isPending}
              css={brandedButtonCss}
            >
              {t('webhooks.webhooksV2.saveAnyway')}
            </Button>
          </Box>
        </Box>
      )}

      {/* API error */}
      {isError && (
        <Text color="fg.error" fontSize="sm">
          {(error && 'message' in error ? error.message : null) ??
            t('webhooks.common.failedToSave')}
        </Text>
      )}

      {/* Save button — hidden while warning is shown */}
      {!warnNoEvents && (
        <Box display="flex" justifyContent="center">
          <Button
            onClick={handleSave}
            loading={isPending}
            css={brandedButtonCss}
          >
            {!integration.remote_id
              ? t('webhooks.common.createIntegration')
              : t('webhooks.common.updateIntegration')}
          </Button>
        </Box>
      )}
    </Box>
  )
}
